import { useEffect, useState } from 'react';
import { useReviews } from '../../hooks/useReviews';

const BUBBLE_TAIL_LEFT = '2.25rem';

function DemoUserIcon() {
  return (
    <svg className="h-6 w-6 text-white sm:h-7 sm:w-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
}

function NavButton({ onClick, label, children, className = '', disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      disabled={disabled}
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[#146599] bg-transparent text-[#146599] transition hover:bg-[#146599]/10 disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:w-8 ${className}`}
    >
      {children}
    </button>
  );
}

function StudentAvatar({ avatar }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showFallback = !avatar?.trim() || imageFailed;

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-gradient-to-br from-[#D4C4F5] to-[#9369CC] shadow-sm sm:h-[52px] sm:w-[52px]">
      {showFallback ? (
        <DemoUserIcon />
      ) : (
        <img
          src={avatar}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      )}
    </div>
  );
}

function StarRating({ rating = 0 }) {
  const value = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));

  return (
    <div className="relative z-[1] flex gap-0.5 text-[#1A87CC]" aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`h-[14px] w-[14px] sm:h-4 sm:w-4 ${star <= value ? 'text-[#1A87CC]' : 'text-[#C5E4F7]'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ item }) {
  return (
    <article className="flex flex-col">
      <div className="relative min-h-[168px] overflow-visible bg-white px-6 pb-7 pt-5 sm:min-h-[180px] sm:px-7 sm:pb-8 sm:pt-6">
        <span
          className="pointer-events-none absolute right-4 top-3 font-serif text-[64px] leading-none text-[#C5E4F7] sm:right-5 sm:top-4 sm:text-[80px]"
          aria-hidden
        >
          &rdquo;
        </span>

        <StarRating rating={item.rating} />

        <p className="relative z-[1] mt-4 line-clamp-5 pr-10 text-sm leading-[1.7] text-[#1B1B1B] sm:mt-5 sm:pr-14 sm:text-[15px]">
          {item.quote}
        </p>

        <span
          className="absolute bottom-0 h-0 w-0 -translate-x-1/2 translate-y-full border-x-[14px] border-t-[16px] border-x-transparent border-t-white"
          style={{ left: BUBBLE_TAIL_LEFT }}
          aria-hidden
        />
      </div>

      <div className="mt-4 flex items-center gap-3" style={{ paddingLeft: `calc(${BUBBLE_TAIL_LEFT} - 1.75rem)` }}>
        <StudentAvatar avatar={item.avatar} />
        <div>
          <p className="text-base font-bold text-[#9369CC] sm:text-[17px]">{item.name}</p>
          <p className="text-sm text-[#1B1B1B]">{item.role}</p>
        </div>
      </div>
    </article>
  );
}

function TestimonialSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="min-h-[168px] animate-pulse bg-white/70 px-6 py-5 sm:min-h-[180px]" />
      <div className="mt-4 flex items-center gap-3" style={{ paddingLeft: `calc(${BUBBLE_TAIL_LEFT} - 1.75rem)` }}>
        <div className="h-12 w-12 animate-pulse rounded-full bg-white/70 sm:h-[52px] sm:w-[52px]" />
        <div className="space-y-2">
          <div className="h-4 w-28 animate-pulse rounded bg-white/70" />
          <div className="h-3 w-20 animate-pulse rounded bg-white/70" />
        </div>
      </div>
    </div>
  );
}

export default function PricingTestimonialsSection() {
  const { reviews, loading, error } = useReviews();
  const [index, setIndex] = useState(0);
  const total = reviews.length;

  useEffect(() => {
    if (index >= total) setIndex(0);
  }, [index, total]);

  const visible =
    total === 0
      ? []
      : total === 1
        ? [reviews[0]]
        : [reviews[index], reviews[(index + 1) % total]];

  const canNavigate = total > 2;
  const goPrev = () => setIndex((current) => (current === 0 ? total - 1 : current - 1));
  const goNext = () => setIndex((current) => (current + 1) % total);

  return (
    <section className="bg-[#C6E8F6] px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-6xl">
        <h2 className="text-center text-2xl font-bold leading-tight text-[#181059] sm:text-[32px] lg:text-[40px]">
          Hear out what our happy student
          <br />
          say about us
        </h2>

        <div className="mt-8 flex items-center justify-between sm:mt-10">
          <NavButton onClick={goPrev} label="Previous testimonials" disabled={!canNavigate}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </NavButton>

          <NavButton onClick={goNext} label="Next testimonials" disabled={!canNavigate}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </NavButton>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-10 sm:mt-8 md:grid-cols-2 md:gap-8 lg:gap-12">
          {loading && !total ? (
            <>
              <TestimonialSkeleton />
              <TestimonialSkeleton />
            </>
          ) : error && !total ? (
            <p className="col-span-full text-center text-sm text-red-600">{error}</p>
          ) : !total ? (
            <p className="col-span-full text-center text-sm text-[#1B1B1B]/70">
              No student reviews available right now.
            </p>
          ) : (
            visible.map((item) => <TestimonialCard key={item.id} item={item} />)
          )}
        </div>
      </div>
    </section>
  );
}
